import React, { useState, FocusEvent } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { ThemeProvider, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { frFR, enUS } from '@mui/x-date-pickers/locales'
import { customDatePickerTheme } from './theme'
import { muiOptions, MuiProps } from '../../../../infrastructure/materialUiThemeProvider'
import { UserSettingsContextContainer } from '../../../../infrastructure/shell/userSettings/userSettingsStore'
import moment, { Moment } from 'moment'
import { t } from '../../../../infrastructure/i18nextHelper'
import { BaseFieldProps } from '../../fieldProps'
import { fieldStatuses } from '../../styles'
import { useDatePickerState } from './state'
import { FieldStatus } from '../../fieldsStatus'

type DatePickerStyleProps = {
    datepicker?
}

type HighlightedDate = {
    date: string
    color: string
}

let localeConfigured = false

let setMomentLocale = (lang: string) => {
    if (!localeConfigured) {
        moment.updateLocale('fr', {
            week: { dow: 1, doy: 4 },
            months: ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'],
            monthsShort: ['Janv.', 'Fevr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Aout', 'Sept.', 'Oct.', 'Nov.', 'Dec.'],
            weekdays: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
            weekdaysMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
            weekdaysShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
        })
        moment.updateLocale('en', { week: { dow: 1, doy: 4 } })
        localeConfigured = true
    }

    moment.locale(lang.toLowerCase().startsWith('fr') ? 'fr' : 'en')
}

type DatePickerProps = {
    date: string | null
    setDate?: ((newDate: string | null) => void) | undefined
    getHighlightedDates?: () => HighlightedDate[]
    auto?: boolean
    errormessage?: string
    maxDate?: string
    label: string
    disableFuture?: boolean
    disableNewStyle?: boolean
    hideUnderline?: boolean
    smallIcon?: boolean
    onLostFocus?: () => void
    classesOverride?: DatePickerStyleProps
    size?: 'small' | 'standard'
} & BaseFieldProps & MuiProps

function _DatePicker({
    classes, classesOverride, date, onLostFocus, setDate, disableFuture, auto, status, onError, disableNewStyle,
    hideUnderline, smallIcon, ...props }: DatePickerProps) {
    setDate = setDate ?? (() => { })
    let state = useDatePickerState(date, setDate as any, onError)
    let settings = UserSettingsContextContainer.useContainer()
    let dateFormat = settings.getDateFormat()
    let highlightedDates = props.getHighlightedDates ? props.getHighlightedDates() : undefined

    let highlight = status === 'info' ? classes.fieldStatusInfo
        : status === 'warning' ? classes.fieldStatusWarning : ''

    let sizeClass = props.size === 'standard' ? classes.standard : props.size === 'small' ? classes.small : ''

    let lang = settings.getLanguage() ?? 'en'
    let isFrench = lang.toLowerCase().startsWith('fr')
    let pickerLocale = isFrench ? 'fr' : 'en'
    let toolbarDateFormat = isFrench ? 'ddd D MMMM' : 'ddd, MMMM D'
    setMomentLocale(lang)
    let localeText: any = {
        ...(isFrench
            ? frFR.components.MuiLocalizationProvider.defaultProps.localeText
            : enUS.components.MuiLocalizationProvider.defaultProps.localeText),
        datePickerToolbarTitle: t('shell.selectDate'),
        todayButtonLabel: isFrench ? t("shell.today") : t("shell.today")
    }

    return (
        <ThemeProvider theme={customDatePickerTheme}>
            <LocalizationProvider key={`datepicker-${pickerLocale}`} dateAdapter={AdapterMoment} adapterLocale={pickerLocale} localeText={localeText} dateLibInstance={moment}>
                <DatePicker
                    value={date ? moment.utc(date) : null}
                    onChange={(newValue: Moment | null) => state.onChange(newValue)}
                    disableFuture={!!disableFuture}
                    maxDate={props.maxDate ? moment(props.maxDate) : undefined}
                    format={dateFormat}
                    dayOfWeekFormatter={(_, day: any) => {
                        if (moment.isMoment(day)) return isFrench ? day.locale('fr').format('dd') : day.format('dd')
                        if (day && day instanceof Date) return isFrench ? moment(day).locale('fr').format('dd') : moment(day).format('dd')

                        return typeof day === 'string' ? day.slice(0, 2) : ''
                    }}
                    closeOnSelect={true}
                    slotProps={{
                        field: { clearable: true, onClear: () => { state.onChange(null) } },
                        day: (ownerState: any) => {
                            let day = ownerState?.day
                            if (!day) return {}
                            let dayValue = moment(day)
                            let isWeekend = dayValue.day() === 0 || dayValue.day() === 6
                            let weekendSx = isWeekend && !ownerState?.selected && !ownerState?.disabled
                                ? {
                                    bgcolor: '#eeeeee',
                                    '&:hover, &:focus': {
                                        bgcolor: '#e0e0e0'
                                    }
                                }
                                : {}
                            let highlightedDay = highlightedDates?.find(x => dayValue.isSame(moment(x.date), 'day'))
                            if (!highlightedDay) return Object.keys(weekendSx).length ? { sx: weekendSx } : {}

                            return {
                                sx: {
                                    ...weekendSx,
                                    bgcolor: highlightedDay.color,
                                    color: '#1a1a1a',
                                    fontWeight: 700,
                                    '&:hover, &:focus': {
                                        bgcolor: highlightedDay.color,
                                        filter: 'brightness(0.95)'
                                    }
                                }
                            }
                        },
                        inputAdornment: {
                            sx: { position: 'relative' },
                        },
                        textField: {
                            className: `${classesOverride ? classesOverride.datepicker : classes.picker} ${highlight} ${sizeClass}`,
                            error: status === 'alert' || state.isOnError,
                            label: props.label,
                            placeholder: props.disabled ? '' : dateFormat,
                            InputLabelProps: { shrink: true },
                            InputProps: {
                                disableUnderline: hideUnderline || props.disabled || false,
                                sx: {
                                    '& .clearButton': {
                                        visibility: 'hidden',
                                        width: 0,
                                        padding: 0,
                                        overflow: 'hidden',
                                    },
                                    '&:hover .clearButton': {
                                        visibility: 'visible',
                                        width: 'auto',
                                        padding: '2px',
                                        overflow: 'visible',
                                    },
                                },
                            },
                            inputProps: { readOnly: true },
                            helperText:
                                status === 'alert' || onError
                                    ? (props.errormessage ? props.errormessage : t('components.error'))
                                    : (auto ? t('components.autoCalculated') : null),
                            FormHelperTextProps: {
                                sx: {
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    m: 0,
                                },
                            },
                            onBlur: onLostFocus,
                            size: props.size === 'small' ? 'small' : 'medium',
                            disabled: props.disabled,
                            variant: !disableNewStyle ? 'filled' : 'standard'
                        },
                        openPickerButton: { size: smallIcon ? 'small' : 'medium' },
                        actionBar: { actions: ['clear', 'cancel', 'accept'], },
                        toolbar: {
                            hidden: false,
                            toolbarFormat: toolbarDateFormat,
                        },
                    }}
                    {...props}
                />
            </LocalizationProvider>
        </ThemeProvider>)
}

type DatePeriod = {
    startDate: string | null
    endDate: string | null
}

type DateRangePickerProps = {
    startDate: string | null
    endDate: string | null
    getHighlightedDates?: () => HighlightedDate[]
    onPeriodChange: (period: DatePeriod) => void
    startLabel: string
    endLabel: string
    classesOverride?: DatePickerStyleProps
    size?: 'small' | 'standard'
    startAuto?: boolean
    endAuto?: boolean
    startDisabled?: boolean
    endDisabled?: boolean
    startStatus?: FieldStatus
    endStatus?: FieldStatus
    disableNewStyle?: boolean
    autoReplace?: boolean
    withoutAutofill?: boolean
} & BaseFieldProps & MuiProps

let _DateRangePicker = ({ classes, classesOverride, startDate, endDate, onPeriodChange, startLabel, endLabel, size, ...props }: DateRangePickerProps) => {
    let handleStartDateChange = (date: string | null) => {
        if ((!props.withoutAutofill && !endDate) || (endDate && moment(date).isAfter(moment(endDate)) && props.autoReplace))
            onPeriodChange({ startDate: date, endDate: date })
        else
            onPeriodChange({ startDate: date, endDate: endDate })
    }

    let handleEndDateChange = (date: string | null) => {
        if ((!props.withoutAutofill && !startDate) || (startDate && moment(date).isBefore(startDate)) && props.autoReplace)
            onPeriodChange({ startDate: date, endDate: date })
        else
            onPeriodChange({ startDate: startDate, endDate: date })
    }

    return (
        <>
            <CustomDatePicker
                key={startLabel}
                label={startLabel}
                classes={classes}
                classesOverride={classesOverride}
                date={startDate}
                getHighlightedDates={props.getHighlightedDates}
                setDate={handleStartDateChange}
                disabled={props.disabled || props.startDisabled}
                disableNewStyle={props.disableNewStyle}
                onError={props.onError}
                status={props.status || props.startStatus}
                size={size}
                auto={props.startAuto}
            />
            <CustomDatePicker
                key={endLabel}
                label={endLabel}
                classes={classes}
                classesOverride={classesOverride}
                date={endDate}
                getHighlightedDates={props.getHighlightedDates}
                setDate={handleEndDateChange}
                disabled={props.disabled || props.endDisabled}
                disableNewStyle={props.disableNewStyle}
                onError={props.onError}
                status={props.status || props.endStatus}
                size={size}
                auto={props.endAuto}
            />
        </>
    )
}

type MonthPickerProps = {
    date: string | null
    setDate: (newDate: string | null) => void
    auto?: boolean
    label?: string
    disablePast?: boolean
    disableNewStyle?: boolean
    onLostFocus?: () => void
    size?: 'small' | 'standard'
    classesOverride?: DatePickerStyleProps
} & BaseFieldProps & MuiProps

let _MonthPicker = ({ classes, classesOverride, date, onLostFocus, setDate, disablePast, auto, onError, disableNewStyle, ...props }: MonthPickerProps) => {
    let state = useDatePickerState(date, setDate, onError)

    let highlight = props.status === 'info' ? classes.fieldStatusInfo
        : props.status === 'warning' ? classes.fieldStatusWarning : ''

    let sizeClass = props.size === 'standard' ? classes.standard : props.size === 'small' ? classes.small : ''

    return (
        <ThemeProvider theme={customDatePickerTheme}>
            <DatePicker
                value={date ? moment(date) : null}
                onChange={(newDate: Moment | null) => {
                    if (newDate?.isValid()) {
                        newDate = newDate.startOf('month')
                        state.onChange(newDate)
                    } else if (!newDate) {
                        state.onChange(null)
                    }
                }}
                views={['year', 'month']}
                openTo="month"
                disablePast={!!disablePast}
                format="MM/YYYY"
                closeOnSelect={false}
                slotProps={{
                    textField: {
                        className: `${classesOverride ? classesOverride.datepicker : classes.picker} ${highlight} ${sizeClass}`,
                        error: props.status === 'alert' || state.isOnError,
                        label: props.label,
                        placeholder: moment().format('MM/YYYY'),
                        InputLabelProps: { shrink: true },
                        InputProps: {
                            disableUnderline: props.disabled,
                        },
                        inputProps: { readOnly: true },
                        helperText: auto ? t('components.autoCalculated') : null,
                        onBlur: onLostFocus,
                        size: props.size === 'small' ? 'small' : 'medium',
                        disabled: props.disabled,
                        variant: !disableNewStyle ? 'filled' : 'standard'
                    },
                    actionBar: { actions: ['clear', 'cancel', 'accept'], },
                    toolbar: {
                        hidden: false,
                    }
                }}
                disabled={props.disabled}
                {...props}
            />
        </ThemeProvider>)
}

type MonthRangePickerProps = {
    classes: any
    classesOverride?: any
    startDate: string | null
    endDate: string | null
    onLostFocus?: (event: FocusEvent<HTMLInputElement>) => void
    onStartDateChange: (newDate: string | null) => void
    onEndDateChange: (newDate: string | null) => void
    disablePast?: boolean
    auto?: boolean
    onError?: boolean
    disableNewStyle?: boolean
    label?: string
    status?: 'info' | 'warning' | 'alert'
    size?: 'standard' | 'small'
}

let _MonthRangePicker = ({ classes, classesOverride, startDate, endDate, onStartDateChange, onEndDateChange, disablePast, auto, onError, disableNewStyle, status, size, label }: MonthRangePickerProps) => {
    let [newStartDate, setNewStartDate] = useState<string | null>(startDate)
    let [newEndDate, setNewEndDate] = useState<string | null>(endDate)
    let handleStartDateChange = (date) => {
        if (!newEndDate || (newEndDate && date > newEndDate)) {
            setNewEndDate(date)
            onEndDateChange(date)
        }
        setNewStartDate(date)
        onStartDateChange(date)
    }

    let handleEndDateChange = (date) => {
        if (!newStartDate || (newStartDate && date < newStartDate)) {
            setNewStartDate(date)
            onStartDateChange(date)
        }
        setNewEndDate(date)
        onEndDateChange(date)
    }

    return (
        <div>
            <div className='container'>
                <label className={classes.label}>{label}</label>
            </div>
            <MonthPicker
                classesOverride={classesOverride}
                date={newStartDate}
                setDate={handleStartDateChange}
                disablePast={disablePast}
                auto={auto}
                onError={onError}
                disableNewStyle={disableNewStyle}
                status={status}
                size={size}
            />
            <MonthPicker
                classesOverride={classesOverride}
                date={newEndDate}
                setDate={handleEndDateChange}
                disablePast={disablePast}
                auto={auto}
                onError={onError}
                disableNewStyle={disableNewStyle}
                status={status}
                size={size}
            />
        </div>
    )
}

let styles = _ =>
    createStyles({
        small: { width: '8em !important' },
        standard: { width: '10.3em !important' },
        picker: {
            width: '15.3em',
            margin: '0em 0.5em',
            "& .Mui-disabled": {
                backgroundColor: 'white',
                "& span": {
                    color: 'white'
                }
            }
        },
        container: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1em'
        },
        label: {
            color: 'rgba(0, 0, 0, 0.54)',
            fontSize: '12px',
            fontFamily: 'Arial, sans-serif',
            padding: '0.5em'
        },
        ...fieldStatuses
    })

export let CustomDatePicker = withStyles(styles, muiOptions)(_DatePicker)
export let DateRangePicker = withStyles(styles, muiOptions)(_DateRangePicker)
export let MonthPicker = withStyles(styles, muiOptions)(_MonthPicker)
export let MonthRangePicker = withStyles(styles, muiOptions)(_MonthRangePicker)
